import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PlanCluster, PlanTopicsResponse } from './inventory-plan-clusters.service';

export interface OrganizePillarDraft {
  topicSlug: string;
  label: string;
  tempId: string;
}

export interface OrganizePinDraft {
  urlId: string;
  topicSlug: string;
}

export interface OrganizeScoreReason {
  code: string;
  label: string;
  weight: number;
}

export interface OrganizeUrlAssignment {
  urlId: string;
  url: string;
  topicSlug: string | null;
  score: number;
  topicClass: 'core' | 'mention' | null;
  topicRelevancePct: number;
  pinned: boolean;
  reasons: OrganizeScoreReason[];
}

export interface OrganizePillarSubtopic {
  topicSlug: string;
  label: string;
  urlCount: number;
  sharePct: number;
}

export interface OrganizePillarComposition {
  mode: 'distinct_subtopics' | 'homogeneous';
  message: string;
  subtopics: OrganizePillarSubtopic[];
  primaryFocusUrlCount: number;
}

export interface OrganizePillarSimilarHint {
  topicSlug: string;
  label: string;
  reason: 'grandparent' | 'inflection' | 'topic_edge' | 'token_overlap';
  reasonLabel: string;
  similarityScore?: number;
}

export interface OrganizePillarPreview {
  topicSlug: string;
  label: string;
  authorityScore?: number;
  urlCount: number;
  urls: OrganizeUrlAssignment[];
  composition: OrganizePillarComposition;
  similarTo?: OrganizePillarSimilarHint[];
}

export interface OrganizePreviewData {
  scopeDomain: string;
  pillars: OrganizePillarPreview[];
  unassigned: OrganizeUrlAssignment[];
  summary: {
    trackedUrlCount: number;
    assignedUrlCount: number;
    unassignedUrlCount: number;
    pillarCount: number;
    demandLabelCount: number;
  };
}

export interface OrganizeProposalPillar {
  topicSlug: string;
  label: string;
  authorityScore: number;
  estimatedUrlCount: number;
  exampleUrls: Array<{
    urlId: string;
    url: string;
    topicRelevancePct: number;
    topicClass: 'core' | 'mention';
  }>;
}

export interface OrganizeProposalMeta {
  suggestedPillarCount: number;
  trackedUrlCount: number;
  minUrlCountApplied: number;
  skippedThinTopics: Array<{ topicSlug: string; label: string; estimatedUrlCount: number }>;
}

export interface OrganizeProposalData {
  scopeDomain: string;
  pillars: OrganizeProposalPillar[];
  meta: OrganizeProposalMeta;
}

export interface CompetitorInspirationTopicRow {
  topicSlug: string;
  label: string;
  ourPageCount: number;
  ourAuthorityScore: number | null;
  alreadyPillar: boolean;
  maxCompetitorPageCount: number;
  gap: number;
  competitors: Array<{
    domain: string;
    ready: boolean;
    pageCount: number | null;
    authorityScore: number | null;
    matchKind: 'exact' | 'inflection' | 'child' | 'none';
  }>;
}

export type CompetitorBucketLabelSource = 'grandparent' | 'highest_gap' | 'single';

export interface CompetitorInspirationBucketRow {
  bucketId: string;
  label: string;
  labelSource: CompetitorBucketLabelSource;
  topicCount: number;
  ourPageCountSum: number;
  maxCompetitorPageCount: number;
  gap: number;
  representativeTopicSlug: string;
  representativeLabel: string;
  alreadyPillar: boolean;
  groupingReasons: string[];
  topics: CompetitorInspirationTopicRow[];
}

export interface CompetitorInspirationData {
  scopeDomain: string;
  competitors: Array<{ domain: string; ready: boolean; topicCount: number }>;
  topics: CompetitorInspirationTopicRow[];
  buckets: CompetitorInspirationBucketRow[];
}

export interface OrganizeStatusData {
  scopeDomain: string;
  ready: boolean;
  blockedReasons: string[];
  hasCommittedPlan: boolean;
  trackedUrlCount: number;
  assignedUrlCount: number;
  demandLabelCount: number;
  topicsMapComplete: boolean;
  topicsMapStale: boolean;
  topicsAggregateComplete: boolean;
}

@Injectable({ providedIn: 'root' })
export class ClustersOrganizeService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  getStatus(activeDomain: string): Observable<{ success: boolean; data: OrganizeStatusData }> {
    const params = new URLSearchParams({ activeDomain });
    return this.http.get<{ success: boolean; data: OrganizeStatusData }>(
      `${this.API_URL}/domains/plan/organize/status?${params.toString()}`
    );
  }

  getProposal(activeDomain: string): Observable<{ success: boolean; data: OrganizeProposalData }> {
    const params = new URLSearchParams({ activeDomain });
    return this.http.get<{ success: boolean; data: OrganizeProposalData }>(
      `${this.API_URL}/domains/plan/organize/proposal?${params.toString()}`
    );
  }

  getCompetitorInspiration(
    activeDomain: string,
    currentPillarSlugs: string[] = []
  ): Observable<{ success: boolean; data: CompetitorInspirationData }> {
    const params = new URLSearchParams({ activeDomain });
    if (currentPillarSlugs.length > 0) {
      params.set('currentPillarSlugs', currentPillarSlugs.join(','));
    }
    return this.http.get<{ success: boolean; data: CompetitorInspirationData }>(
      `${this.API_URL}/domains/plan/organize/competitor-inspiration?${params.toString()}`
    );
  }

  preview(
    activeDomain: string,
    pillars: Array<{ topicSlug: string; label: string }>,
    pins: OrganizePinDraft[] = []
  ): Observable<{ success: boolean; data: OrganizePreviewData }> {
    return this.http.post<{ success: boolean; data: OrganizePreviewData }>(
      `${this.API_URL}/domains/plan/organize/preview`,
      { activeDomain, pillars, pins }
    );
  }

  apply(
    activeDomain: string,
    pillars: Array<{ topicSlug: string; label: string }>,
    pins: OrganizePinDraft[] = [],
    mode: 'replace' | 'merge' = 'replace'
  ): Observable<PlanTopicsResponse & { data: PlanTopicsResponse['data'] & { appliedUrlCount: number; pillarCount: number } }> {
    return this.http.post<any>(`${this.API_URL}/domains/plan/organize/apply`, {
      activeDomain,
      pillars,
      pins,
      mode
    });
  }

  getChildSlugs(activeDomain: string, parentTopicSlug: string): Observable<{ success: boolean; data: { childSlugs: string[] } }> {
    const params = new URLSearchParams({ activeDomain, parentTopicSlug });
    return this.http.get<{ success: boolean; data: { childSlugs: string[] } }>(
      `${this.API_URL}/domains/plan/organize/child-slugs?${params.toString()}`
    );
  }

  newTempId(): string {
    return `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  pillarsFromPlanTopics(topics: PlanCluster[]): OrganizePillarDraft[] {
    return topics.map((t) => ({
      topicSlug: t.topicSlug,
      label: t.label,
      tempId: this.newTempId()
    }));
  }

  pinsFromPlanTopics(topics: PlanCluster[]): OrganizePinDraft[] {
    const pins: OrganizePinDraft[] = [];
    for (const topic of topics) {
      for (const url of topic.urls || []) {
        pins.push({ urlId: url.urlId, topicSlug: topic.topicSlug });
      }
    }
    return pins;
  }
}
